export type Action<TPayload = undefined,
    TMeta = undefined> = TPayload extends undefined
    ? (TMeta extends undefined ? { type: string } : { type: string; meta: TMeta })
    : (TPayload extends Error
        ? (TMeta extends undefined
            ? { type: string; payload: TPayload; error: true }
            : { type: string; payload: TPayload; meta: TMeta; error: true })
        : (TMeta extends undefined
            ? { type: string; payload: TPayload }
            : { type: string; payload: TPayload; meta: TMeta }))

/**
 * Minimal (type-only) action factory
 * @example
 * const clearTodos = action('[Todo] truncate');
 */
export function createAction(type: string): Action
/**
 * Action with error factory
 * @example
 * const fetchTodosRejected = (payload: Error) => action('[Todo] fetch rejected', payload);
 */
export function createAction<TPayload extends Error>(
    type: string,
    payload: TPayload,
): Action<TPayload>
/**
 * Action with non-error payload factory
 * @example
 * const addTodo = ({ name, completed = false }: Todo) => action('[Todo] add', { name, completed });
 */
export function createAction<TPayload>(
    type: string,
    payload: TPayload,
): Action<TPayload>
/**
 * Action with error and meta factory
 * @example
 * const fetchTodosRejected = (payload: Error, meta?: Meta) => action('[Todo] fetch rejected', payload, meta);
 */
export function createAction<TPayload extends Error,
    TMeta>(type: string, payload: TPayload, meta: TMeta): Action<TPayload, TMeta>
/**
 * Action with payload and meta factory
 * @example
 * const addTodo = ({ name, completed = false }: Todo, meta?: Meta) => action('[Todo] add', { name, completed }, meta);
 */
export function createAction<TPayload, TMeta>(
    type: string,
    payload: TPayload,
    meta: TMeta,
): Action<TPayload, TMeta>

/**
 * Flux standard action factory
 * @example
 * const clearTodos = action('[Todo] truncate');
 * @example
 * const fetchTodosRejected = (payload: Error) => action('[Todo] fetch rejected', payload);
 * @example
 * const addTodo = ({ name, completed = false }: Todo) => action('[Todo] add', { name, completed });
 * @example
 * const fetchTodosRejected = (payload: Error, meta?: Meta) => action('[Todo] fetch rejected', payload, meta);
 * @example
 * const addTodo = ({ name, completed = false }: Todo, meta?: Meta) => action('[Todo] add', { name, completed }, meta);
 */
export function createAction<TPayload, TMeta>(
    type: string,
    payload?: TPayload,
    meta?: TMeta,
) {
    return {
        type,
        ...(payload !== undefined ? {payload} : {}),
        ...(meta !== undefined ? {meta} : {}),
        ...(payload instanceof Error ? {error: true} : {}),
    };
}
